import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, CircularProgress, Typography, Chip,
    TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
    Stack, Paper, useMediaQuery, useTheme, Tooltip, alpha, IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import QuickCategorySelectDialog from '../QuickCategorySelectDialog';

function TransactionDrilldownDialog({
    open,
    onClose,
    dialogType,
    selectedYear,
    loading,
    entries = [],
    categories = [],
    onTransactionUpdated,
    ledgerId,
    categoryName,
    month,
    monthName,
    totalAmount
}) {
    const { t, i18n } = useTranslation();
    const { formatCurrency, api } = useAuth();
    const { showToast } = useToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [selectedTxForCategory, setSelectedTxForCategory] = useState(null);
    const [isSavingCategory, setIsSavingCategory] = useState(false);

    const handleSelectCategory = async (category) => {
        if (!selectedTxForCategory) return;
        const entry = selectedTxForCategory;
        setIsSavingCategory(true);
        try {
            const newCatId = category?.id ? Number(category.id) : null;
            const payload = {
                description: entry.description,
                amount: entry.amount,
                currency: entry.currency || 'USD',
                date: entry.date,
                category_id: newCatId,
                recurring_transaction_id: entry.recurring_transaction_id || null,
                credit_card_id: entry.credit_card_id || null,
                source_document_id: entry.source_document_id || null,
                cash_back_amount: entry.cash_back_amount || null,
            };

            await api.put(`/ledger/${entry.id}`, payload);
            setSelectedTxForCategory(null);
            showToast(t('Category updated successfully'), 'success');

            if (onTransactionUpdated) {
                onTransactionUpdated();
            }
        } catch (err) {
            console.error('Failed to update category:', err);
            showToast(err.response?.data?.detail || t('Failed to update category'), 'error');
        } finally {
            setIsSavingCategory(false);
        }
    };

    // Calculate total spending/income amount
    const calculatedTotal = useMemo(() => {
        return entries.reduce((sum, row) => {
            const amt = Number(row.amount || 0);
            if (dialogType === 'income') {
                return sum + (amt < 0 ? Math.abs(amt) : -amt);
            }
            return sum + amt;
        }, 0);
    }, [entries, dialogType]);

    const displayTotal = totalAmount != null ? totalAmount : calculatedTotal;

    // Group entries by individual category when category is 'Other'
    const groupedEntries = useMemo(() => {
        if (categoryName !== 'Other' || !entries || entries.length === 0) return null;
        const groups = {};
        entries.forEach(entry => {
            const catName = entry.category?.name || 'Uncategorized';
            if (!groups[catName]) {
                groups[catName] = {
                    name: catName,
                    category: entry.category || { name: 'Uncategorized', color: '#64748B' },
                    total: 0,
                    entries: []
                };
            }
            groups[catName].entries.push(entry);
            const amt = Number(entry.amount || 0);
            if (dialogType === 'income') {
                groups[catName].total += (amt < 0 ? Math.abs(amt) : -amt);
            } else {
                groups[catName].total += amt;
            }
        });
        return Object.values(groups).sort((a, b) => b.total - a.total);
    }, [entries, categoryName, dialogType]);

    // Build dynamic dialog title
    let titleText = '';
    if (categoryName === 'Other') {
        titleText = monthName
            ? `${t(monthName)} ${t('Spending')}: ${t('Other')}`
            : `${t('Spending')}: ${t('Other')} (${selectedYear})`;
    } else if (categoryName) {
        titleText = monthName
            ? `${t(monthName)} ${t('Spending')}: ${t(categoryName)}`
            : `${t('Spending')}: ${t(categoryName)} (${selectedYear})`;
    } else {
        titleText = dialogType === 'income'
            ? `${t('Income Transactions')} (${selectedYear})`
            : `${t('Spending Transactions')} (${selectedYear})`;
    }

    const renderCategoryChip = (row) => (
        <Tooltip title={t('Click to change category')} arrow placement="top">
            <Chip
                size="small"
                icon={<EditIcon sx={{ fontSize: '0.8rem !important', opacity: 0.75 }} />}
                label={row.category ? t(row.category.name) : t('Uncategorized')}
                onClick={() => setSelectedTxForCategory(row)}
                variant={row.category ? 'outlined' : 'filled'}
                sx={{
                    cursor: 'pointer',
                    bgcolor: row.category?.color ? `${row.category.color}15` : (row.category ? 'transparent' : alpha('#000', 0.06)),
                    color: row.category?.color || (row.category ? 'primary.main' : 'text.secondary'),
                    borderColor: row.category?.color || (row.category ? 'primary.main' : 'divider'),
                    fontSize: '0.75rem',
                    height: 24,
                    fontStyle: row.category ? 'normal' : 'italic',
                    fontWeight: 600,
                    '&:hover': {
                        bgcolor: row.category?.color ? `${row.category.color}25` : alpha('#000', 0.12),
                        transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.15s ease'
                }}
            />
        </Tooltip>
    );

    const renderAmount = (row) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                        color: Number(row.amount) >= 0 ? 'text.primary' : 'success.main'
                    }}
                >
                    {formatCurrency(row.amount, row.currency)}
                </Typography>
                <Typography component="span" variant="caption" sx={{ opacity: 0.6, fontWeight: 600 }}>
                    {row.currency || 'USD'}
                </Typography>
            </Box>
            {row.exchanged_amount && (row.currency || 'USD') !== row.exchanged_currency && (
                <Typography
                    variant="caption"
                    sx={{
                        fontWeight: 600,
                        color: 'text.secondary',
                        opacity: 0.7,
                        mt: 0.25
                    }}
                >
                    ≈ {formatCurrency(row.exchanged_amount, row.exchanged_currency)} {row.exchanged_currency}
                </Typography>
            )}
        </Box>
    );

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
                <DialogTitle sx={{ backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', py: 2, px: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1.1rem' }}>
                                {titleText}
                            </Typography>
                            {entries.length > 0 && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, mt: 0.5, display: 'block' }}>
                                    {formatCurrency(displayTotal)} • {entries.length} {entries.length === 1 ? t('transaction') : t('transactions')}
                                    {groupedEntries && ` (${groupedEntries.length} ${t('categories')})`}
                                </Typography>
                            )}
                        </Box>
                        <IconButton size="small" onClick={onClose} aria-label={t("Close")} sx={{ color: 'text.secondary', ml: 1 }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
                            <CircularProgress />
                        </Box>
                    ) : entries.length === 0 ? (
                        <Box sx={{ py: 6, textAlign: 'center' }}>
                            <Typography color="text.secondary">{t('No data available')}</Typography>
                        </Box>
                    ) : isMobile ? (
                        <Stack spacing={2} sx={{ p: 2 }}>
                            {groupedEntries ? (
                                groupedEntries.map(group => (
                                    <Paper key={group.name} elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 1.5, overflow: 'hidden' }}>
                                        <Box sx={{
                                            py: 1,
                                            px: 2,
                                            bgcolor: alpha(group.category?.color || '#64748B', 0.08),
                                            borderBottom: '1px solid #E2E8F0',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: group.category?.color || '#64748B' }} />
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                    {t(group.name)}
                                                </Typography>
                                                <Chip
                                                    label={`${group.entries.length}`}
                                                    size="small"
                                                    sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: 'background.paper' }}
                                                />
                                            </Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: '"Manrope", sans-serif' }}>
                                                {formatCurrency(group.total)}
                                            </Typography>
                                        </Box>
                                        <Stack spacing={1.5} sx={{ p: 1.5 }}>
                                            {group.entries.map(row => (
                                                <Paper key={row.id} elevation={0} sx={{ p: 1.5, border: '1px solid #F1F5F9', borderRadius: 1 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                                            {new Date(row.date).toLocaleDateString(i18n.language, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>{row.description}</Typography>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        {renderCategoryChip(row)}
                                                        {renderAmount(row)}
                                                    </Box>
                                                </Paper>
                                            ))}
                                        </Stack>
                                    </Paper>
                                ))
                            ) : (
                                entries.map(row => (
                                    <Paper key={row.id} elevation={0} sx={{ p: 2, border: '1px solid #E2E8F0' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                                {new Date(row.date).toLocaleDateString(i18n.language, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>{row.description}</Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            {renderCategoryChip(row)}
                                            {renderAmount(row)}
                                        </Box>
                                    </Paper>
                                ))
                            )}
                        </Stack>
                    ) : groupedEntries ? (
                        <Box sx={{ p: 2.5 }}>
                            <Stack spacing={2.5}>
                                {groupedEntries.map(group => (
                                    <Paper
                                        key={group.name}
                                        elevation={0}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1.5,
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {/* Category Group Header */}
                                        <Box sx={{
                                            py: 1.25,
                                            px: 2.5,
                                            bgcolor: alpha(group.category?.color || '#64748B', 0.08),
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    bgcolor: group.category?.color || '#64748B'
                                                }} />
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                                    {t(group.name)}
                                                </Typography>
                                                <Chip
                                                    label={`${group.entries.length} ${group.entries.length === 1 ? t('transaction') : t('transactions')}`}
                                                    size="small"
                                                    sx={{
                                                        height: 20,
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600,
                                                        bgcolor: 'background.paper',
                                                        color: 'text.secondary'
                                                    }}
                                                />
                                            </Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontFamily: '"Manrope", sans-serif' }}>
                                                {formatCurrency(group.total)}
                                            </Typography>
                                        </Box>

                                        {/* Transactions table */}
                                        <TableContainer>
                                            <Table size="small">
                                                <TableBody>
                                                    {group.entries.map(row => (
                                                        <TableRow key={row.id} hover>
                                                            <TableCell sx={{ color: 'text.secondary', fontWeight: 500, width: 120 }}>
                                                                {new Date(row.date).toLocaleDateString(i18n.language, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })}
                                                            </TableCell>
                                                            <TableCell sx={{ fontWeight: 500 }}>{row.description}</TableCell>
                                                            <TableCell sx={{ width: 180 }}>
                                                                {renderCategoryChip(row)}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ width: 140 }}>
                                                                {renderAmount(row)}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>
                                ))}
                            </Stack>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead sx={{ backgroundColor: '#F8FAFC' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600, py: 2 }}>{t('Date')}</TableCell>
                                        <TableCell sx={{ fontWeight: 600, py: 2 }}>{t('Description')}</TableCell>
                                        <TableCell sx={{ fontWeight: 600, py: 2 }}>{t('Category')}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>{t('Amount')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {entries.map(row => (
                                        <TableRow key={row.id} hover>
                                            <TableCell sx={{ color: 'text.secondary', fontWeight: 500, width: 120 }}>
                                                {new Date(row.date).toLocaleDateString(i18n.language, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 500 }}>{row.description}</TableCell>
                                            <TableCell sx={{ width: 180 }}>
                                                {renderCategoryChip(row)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ width: 140 }}>
                                                {renderAmount(row)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #E2E8F0' }}>
                    <Button onClick={onClose} variant="contained" color="primary" disableElevation>
                        {t('Close')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Category Quick Picker Dialog ── */}
            <QuickCategorySelectDialog
                open={Boolean(selectedTxForCategory)}
                onClose={() => setSelectedTxForCategory(null)}
                currentCategoryId={selectedTxForCategory?.category_id || selectedTxForCategory?.category?.id || null}
                categories={categories}
                onSelectCategory={handleSelectCategory}
                isSaving={isSavingCategory}
            />
        </>
    );
}

export default React.memo(TransactionDrilldownDialog);
